import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useProcurementPurchaseOrdersInvoicesDatatable,
} from 'generated/resources/datatable.graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import ResourceInvoices from 'modules/finance/components/ResourceInvoices'

interface Props {
  purchaseOrderId: string
  vendorId: string | undefined
}

const HEADER_OFFSET = 85

const Attachments = ({ purchaseOrderId, vendorId }: Props) => {
  const {
    company,
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()

  const source = useProcurementPurchaseOrdersInvoicesDatatable(
    [
      {
        identifier: 'documentNumber',
        title: i18n.t('procurement.purchase_orders.detail.attachments.table.invoice_number'),
        kind: ProjectionKind.Path,
        path: 'financialDocument_documentNumber',
      },
      {
        identifier: 'documentDate',
        title: i18n.t('procurement.purchase_orders.detail.attachments.table.document_date'),
        kind: ProjectionKind.Path,
        path: 'financialDocument_documentDate',
      },
      {
        identifier: 'amount',
        kind: ProjectionKind.Custom,
        title: i18n.t('procurement.purchase_orders.detail.attachments.table.amount'),
        template: '{{ financialDocument_totalAmountCents | divided_by: 100.0 }}',
        textAlign: 'right',
        justifyContent: 'flexEnd',
        render: ({ financialDocument_totalAmountCents, financialDocument_currency }) => {
          return (
            <Box alignItems="flexEnd">
              {financialDocument_totalAmountCents && financialDocument_currency && (
                <Text.Normal size="200" weight="medium">
                  {formattedAmount({
                    amountCents: Number(financialDocument_totalAmountCents),
                    currency: financialDocument_currency,
                    locale,
                  })}
                </Text.Normal>
              )}
            </Box>
          )
        },
      },
      {
        identifier: 'detail',
        kind: ProjectionKind.Decorative,
        render: ({ financialDocument_id }) => (
          <Button.Router
            type="secondary"
            to={navigation.finance.invoices.list.invoiceId.fullPath({
              invoiceId: String(financialDocument_id),
            })}
            iconName="arrow-right"
          />
        ),
      },
    ],
    {
      variables: { ids: [purchaseOrderId], companyId: company.id },
    }
  )

  return (
    <Box
      padding="s24"
      overflowY="scroll"
      style={{
        height: `calc(100vh - ${HEADER_OFFSET}px)`,
      }}
    >
      <Text.Normal size="200">
        {i18n.t('procurement.purchase_orders.detail.attachments.subtitle')}
      </Text.Normal>

      <Box paddingTop={'s16'} paddingBottom={'s16'}>
        <ResourceInvoices
          resource={{ id: purchaseOrderId, type: 'purchaseorder' }}
          source={source}
          emptyState={
            <>
              <Icon.Large icon="document-folder-drawer-2" color="grey600" />
              <Text.Normal color="grey800">
                {i18n.t('procurement.purchase_orders.detail.attachments.empty')}
              </Text.Normal>
            </>
          }
          vendorId={vendorId}
        />
      </Box>
    </Box>
  )
}

export default Attachments
